import { formatNumber, formatDollars } from '~/utils';
import { ProjectInvestorQueue } from '~/types/project';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { 
  InvestorBidStatusDisplay, 
  InvestorBidStatus,
  FeeUnits,
  FeeUnitsDisplayShort,
  StorageFeeUnitsDisplayShort,
  UnitDecimals,
  StorageFeeUnits,
  ProjectStatus,
} from '~/constants';

export interface InvestorQueueDisplayValues {
  name: string | undefined
  actionStatus: keyof typeof InvestorBidStatus
  buyerFee: string
  buyerStorageFee: string
  formattedStatus: string
  sellerFee: string
  sellerStorageFee: string
  sentAt?: string
  updatedAt?: string
}

const investorQueueDisplayValues = (
  investorRow: ProjectInvestorQueue,
  projectStatus: string | null,
  hasPortfolioInvestorDeclined: boolean,
  hasPortfolioConversationEnded: boolean
): InvestorQueueDisplayValues => {
    const { 
      nrsFee, 
      nrsFeeUnit,
      buyerStorageFee, 
      buyerStorageFeeUnit,
      sellerFee, 
      sellerFeeUnit,
      sellerStorageFee,
      sellerStorageFeeUnit, 
      status,
      investor,
      sentAt,
      updatedAt 
    } = investorRow;

    const sentAtDate = sentAt ? parseISO(sentAt) : null;
    const updatedAtDate = updatedAt ? parseISO(updatedAt) : null;
    
    const date = status === InvestorBidStatus.INVITED ? sentAtDate : updatedAtDate;
    
    const formattedDate = date ? format(date, 'MM/dd/yyyy') : '';
    
    let formattedStatus: string = InvestorBidStatusDisplay[status as keyof typeof InvestorBidStatus];
    if (status === InvestorBidStatus.INVITED || status === InvestorBidStatus.ACCEPTED) {
      formattedStatus = `${formattedStatus} ${formattedDate}`;
    }

    if (projectStatus === ProjectStatus.DECLINED_TO_BID || hasPortfolioInvestorDeclined) {
      formattedStatus = 'Declined to bid';
    }
    if (projectStatus === ProjectStatus.BID_REJECTED || hasPortfolioConversationEnded) {
      formattedStatus = 'Conversation ended';
    }

    let actionStatus = status;
    if (hasPortfolioInvestorDeclined || hasPortfolioConversationEnded) {
      actionStatus = InvestorBidStatus.DECLINED;
    }

    return {
      name: investor && investor.company?.name,
      buyerFee: nrsFee > 0 
      ? `${nrsFeeUnit === FeeUnits.DOLLARS_PER_WDC || nrsFeeUnit === FeeUnits.FLAT_FEE
          ? formatDollars(nrsFee, { dec: nrsFeeUnit === FeeUnits.DOLLARS_PER_WDC ? UnitDecimals.WDC : UnitDecimals.TOTAL }) 
          : formatNumber(nrsFee, UnitDecimals.PERCENT, { default0: '—', suffix: '%' })} ${nrsFeeUnit ? FeeUnitsDisplayShort[nrsFeeUnit] : ''}`
      : '—',
      buyerStorageFee: `${formatDollars(buyerStorageFee, { dec: buyerStorageFeeUnit === StorageFeeUnits.DOLLARS_PER_KWH ? UnitDecimals.KWH_BESS : UnitDecimals.TOTAL })}  ${buyerStorageFee && buyerStorageFeeUnit ? StorageFeeUnitsDisplayShort[buyerStorageFeeUnit] : ''}`,
      sellerFee: `${sellerFeeUnit === FeeUnits.DOLLARS_PER_WDC || sellerFeeUnit === FeeUnits.FLAT_FEE 
        ? formatDollars(sellerFee, { dec: sellerFeeUnit === FeeUnits.DOLLARS_PER_WDC ? UnitDecimals.WDC : UnitDecimals.TOTAL })
        : formatNumber(sellerFee, UnitDecimals.PERCENT, { default0: '—', suffix: '%' })} ${sellerFeeUnit ? FeeUnitsDisplayShort[sellerFeeUnit] : ''}`,
      sellerStorageFee: `${formatDollars(sellerStorageFee, { dec: sellerStorageFeeUnit === StorageFeeUnits.DOLLARS_PER_KWH ? UnitDecimals.KWH_BESS : UnitDecimals.TOTAL })}  ${sellerStorageFee && sellerStorageFeeUnit ? StorageFeeUnitsDisplayShort[sellerStorageFeeUnit] : ''}`,
      formattedStatus,
      actionStatus,
    };
  };

export default investorQueueDisplayValues;